import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchStatsAsync,
  setStatsYear,
  STATS_YEAR_MIN,
  DEFAULT_STATS_CHART,
  StatsMetric,
  StatsChartView,
  StatsChartMeta,
} from '../store/slices/statsSlice';
import LoadingSpinner from './LoadingSpinner';
import Select, { SelectOption } from './Select';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
  Cell,
  ReferenceLine,
  ComposedChart,
  Line,
  LineChart,
} from 'recharts';
import {
  buildGroupedMonthlyRows,
  buildStackedPredictionRows,
  buildRateRows,
  RateRow,
  buildStatusStackRows,
  BreakdownRow,
  buildSourceRows,
  SourceRow,
  rateKey,
  buildSpendRows,
  SpendRow,
} from '../utils/monthlyBarChart';
import { STATUSES, STATUS_LABELS, STATUS_CHART_COLORS, ConsultationStatus } from '../constants/consultationStatus';
import './GroupForm.css';
import './Students.css';
import './Stats.css';

const SELECTOR_SEGMENT_LIMIT = 5;
const BELOW_AVERAGE_COLOR = '#e53e3e';

// Whole dollars: ad budgets aren't meaningful below $1.
const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function formatValue(value: number, unit: StatsMetric['unit']): string {
  if (unit === 'BYN') return `${value.toFixed(2)} BYN`;
  if (unit === 'per-100-usd') return `${value.toFixed(1)} / $100`;
  return String(value);
}

interface GroupedTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: Record<string, unknown> }>;
  metrics: StatsMetric[];
  unit: StatsMetric['unit'];
  showNet?: boolean;
}

const GroupedTooltip: React.FC<GroupedTooltipProps> = ({
  active, label, payload, metrics, unit, showNet,
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const values = metrics.map((m) => Number(row[m.slug] ?? 0));
  const net = showNet && metrics.length === 2 && unit === 'count'
    ? values[0] - values[1]
    : null;
  return (
    <div className="stats-tooltip">
      <div className="stats-tooltip__label">{label}</div>
      {metrics.map((m) => (
        <div key={m.slug} className="stats-tooltip__row" style={{ color: m.color }}>
          {m.seriesLabel}: {formatValue(Number(row[m.slug] ?? 0), unit)}
        </div>
      ))}
      {net != null && (
        <div className="stats-tooltip__row stats-tooltip__row--net">
          Net: {net > 0 ? '+' : ''}{net}
        </div>
      )}
    </div>
  );
};

interface StackedTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: { value: number; predicted: number; predictedTotal: number } }>;
}

const StackedTooltip: React.FC<StackedTooltipProps> = ({ active, label, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="stats-tooltip">
      <div className="stats-tooltip__label">{label}</div>
      <div className="stats-tooltip__row stats-tooltip__row--collected">
        Collected: {row.value.toFixed(2)} BYN
      </div>
      {row.predicted > 0 && (
        <div className="stats-tooltip__row stats-tooltip__row--predicted">
          Predicted: {row.predictedTotal.toFixed(2)} BYN
        </div>
      )}
    </div>
  );
};

// Up to 2 decimals, trailing zeros trimmed (1.05, 0.7, 2).
const rate = (v: number) => String(Number(v.toFixed(2)));

interface RateTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: RateRow }>;
  metrics: StatsMetric[];
  conversionLabels: Record<string, string>;
  costLabels: Record<string, string>;
}

export const RateTooltip: React.FC<RateTooltipProps> = ({
  active, label, payload, metrics, conversionLabels, costLabels,
}) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="stats-tooltip">
      <div className="stats-tooltip__label">{label}</div>
      {!row.hasAds ? (
        <div className="stats-tooltip__row stats-tooltip__row--muted">No sales ads</div>
      ) : (
        <>
          {metrics.map((m) => {
            const cell = row.cells[m.slug];
            const cellRate = cell?.rate;
            const what = conversionLabels[m.slug] ?? 'conversions';
            const median = m.extras?.median;
            const best = m.extras?.best;
            const rated = cellRate != null;
            const below = median != null && rated && cellRate < median;
            return (
              <div key={m.slug} className="stats-tooltip__block">
                <div
                  className={`stats-tooltip__row stats-tooltip__row--cost ${below ? 'stats-tooltip__row--below' : ''}`}
                  style={below ? undefined : { color: m.color }}
                >
                  {m.seriesLabel}: {rated ? rate(cellRate) : '—'}
                </div>
                <div className="stats-tooltip__row stats-tooltip__row--muted">
                  {cell?.conversions ?? 0} {what}
                  {!rated && (cell?.conversions ?? 0) > 0 ? ', no spend this month' : ''}
                </div>
                {cell?.cost != null && (
                  <div className="stats-tooltip__row stats-tooltip__row--muted">
                    {costLabels[m.slug] ?? 'Cost'} {usd.format(cell.cost)}
                  </div>
                )}
                {median != null && (
                  <div className="stats-tooltip__row stats-tooltip__row--muted">
                    Median {rate(median)} {what} / $100
                  </div>
                )}
                {best != null && (
                  <div className="stats-tooltip__row stats-tooltip__row--muted">
                    Best month {rate(best)} {what} / $100
                  </div>
                )}
              </div>
            );
          })}
          <div className="stats-tooltip__row">Spent: {usd.format(row.spend)}</div>
          <div className="stats-tooltip__row stats-tooltip__row--muted">Sales ads: {row.ads}</div>
        </>
      )}
    </div>
  );
};

/**
 * Results per $100 (higher is better), one toggleable series per metric.
 * The dashed line is the median month — worse than that is faded and outlined
 * red. The dotted line is the best month, for comparison only: a month can
 * sit under the best and still be fine. Spend with zero results is a real 0.
 */
const RateChart: React.FC<{ chart: StatsChartView; year: number }> = ({ chart, year }) => {
  const [hidden, setHidden] = useState<string[]>([]);
  const visibleMetrics = useMemo(
    () => chart.metrics.filter((m) => !hidden.includes(m.slug)),
    [chart.metrics, hidden],
  );
  const rows = useMemo(() => buildRateRows(chart.metrics, year), [chart.metrics, year]);
  const conversionLabels = chart.options.conversionLabels ?? {};
  const costLabels = chart.options.costLabels ?? {};

  // At least one series stays on — an empty chart answers nothing.
  const toggle = (slug: string) => {
    setHidden((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      return visibleMetrics.length > 1 ? [...prev, slug] : prev;
    });
  };

  return (
    <>
      <div className="stats-series-toggle" role="group" aria-label="Series">
        {chart.metrics.map((m) => {
          const on = !hidden.includes(m.slug);
          return (
            <button
              key={m.slug}
              type="button"
              className={`stats-series-toggle__btn ${on ? 'is-on' : ''}`}
              aria-pressed={on}
              disabled={on && visibleMetrics.length === 1}
              onClick={() => toggle(m.slug)}
              title={m.label}
            >
              <span className="stats-series-toggle__swatch" style={{ background: on ? m.color : 'transparent', borderColor: m.color }} />
              {m.seriesLabel}
            </button>
          );
        })}
        <span className="stats-series-toggle__key">
          <span className="stats-series-toggle__avg" aria-hidden="true" />
          Median
        </span>
        <span className="stats-series-toggle__key">
          <span className="stats-series-toggle__best" aria-hidden="true" />
          Best month
        </span>
        <span className="stats-series-toggle__key">
          <span className="stats-series-toggle__below" aria-hidden="true" />
          Below median
        </span>
      </div>
      <div className="stats-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 20, right: 12, left: 8, bottom: 56 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 11 }}
              angle={-32}
              textAnchor="end"
              height={52}
              interval={0}
            />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => rate(Number(v))} width={40} />
            <Tooltip
              content={<RateTooltip metrics={visibleMetrics} conversionLabels={conversionLabels} costLabels={costLabels} />}
            />
            {visibleMetrics.map((m) => {
              const median = m.extras?.median ?? null;
              return (
                <Bar
                  key={m.slug}
                  dataKey={m.slug}
                  fill={m.color}
                  name={m.seriesLabel}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={36}
                  isAnimationActive={false}
                >
                  {rows.map((r) => {
                    const cell = r.cells[m.slug];
                    const below = median != null && cell?.rate != null && cell.rate < median;
                    return (
                      <Cell
                        key={r.monthKey}
                        fillOpacity={below ? 0.35 : 1}
                        stroke={below ? BELOW_AVERAGE_COLOR : undefined}
                        strokeWidth={below ? 1.5 : 0}
                      />
                    );
                  })}
                  <LabelList
                    position="top"
                    fill={BELOW_AVERAGE_COLOR}
                    fontSize={11}
                    valueAccessor={(entry: { payload: RateRow }) => (
                      entry.payload.cells[m.slug]?.rate === 0 ? '0' : ''
                    )}
                  />
                </Bar>
              );
            })}
            {visibleMetrics.map((m) => (
              m.extras?.median != null && (
                <ReferenceLine
                  key={`median-${m.slug}`}
                  y={m.extras.median}
                  stroke={m.color}
                  strokeDasharray="6 4"
                  ifOverflow="extendDomain"
                />
              )
            ))}
            {visibleMetrics.map((m) => (
              m.extras?.best != null && m.extras.best !== m.extras.median && (
                <ReferenceLine
                  key={`best-${m.slug}`}
                  y={m.extras.best}
                  stroke={m.color}
                  strokeDasharray="1 3"
                  strokeOpacity={0.85}
                  ifOverflow="extendDomain"
                />
              )
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

const NO_STATUS = 'none';
const NO_STATUS_COLOR = '#cbd5e0';

const statusLabel = (key: string) =>
  key === NO_STATUS ? 'Not set' : STATUS_LABELS[key as ConsultationStatus] ?? key;
const statusColor = (key: string) =>
  key === NO_STATUS ? NO_STATUS_COLOR : STATUS_CHART_COLORS[key as ConsultationStatus] ?? NO_STATUS_COLOR;

const SOURCE_ORDER = ['ads', 'organic', 'referral', 'unattributed'];
const SOURCE_LABELS: Record<string, string> = {
  ads: 'Ads',
  organic: 'Organic',
  referral: 'Referral',
  unattributed: 'Unattributed',
};
const SOURCE_COLORS: Record<string, string> = {
  ads: '#667eea',
  organic: '#10b981',
  referral: '#d69e2e',
  unattributed: '#a0aec0',
};

const pct = (v: number) => `${Math.round(v)}%`;

/** No paid-rate line for consultations without a source — the rate says nothing about a channel. */
const RATE_SOURCES = ['ads', 'organic', 'referral'];

const StatusTooltip: React.FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: BreakdownRow }>;
  keys: string[];
}> = ({ active, label, payload, keys }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  return (
    <div className="stats-tooltip">
      <div className="stats-tooltip__label">{label}</div>
      {keys.filter((k) => Number(row[k]) > 0).map((k) => (
        <div key={k} className="stats-tooltip__row" style={{ color: statusColor(k) }}>
          {statusLabel(k)}: {row[k]}
        </div>
      ))}
      <div className="stats-tooltip__row stats-tooltip__row--net">Total: {row.total}</div>
    </div>
  );
};

const SourceTooltip: React.FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: SourceRow }>;
  keys: string[];
}> = ({ active, label, payload, keys }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const shown = keys.filter((k) => row.cells[k]?.total > 0);
  return (
    <div className="stats-tooltip">
      <div className="stats-tooltip__label">{label}</div>
      {shown.length === 0 && (
        <div className="stats-tooltip__row stats-tooltip__row--muted">No consultations</div>
      )}
      {shown.map((k) => {
        const c = row.cells[k];
        return (
          <div key={k} className="stats-tooltip__row" style={{ color: SOURCE_COLORS[k] }}>
            {SOURCE_LABELS[k] ?? k}: {c.total}, {c.paid} paid ({pct((c.paid / c.total) * 100)})
            {c.open > 0 && <span className="stats-tooltip__muted"> · {c.open} undecided</span>}
          </div>
        );
      })}
    </div>
  );
};

type ConsultationsView = 'status' | 'source';

/**
 * Consultations per month in two views: stacked by current status, or
 * grouped by source with each source's paid rate on a right-hand % axis.
 */
const ConsultationsBreakdownChart: React.FC<{ chart: StatsChartView; year: number }> = ({ chart, year }) => {
  const [view, setView] = useState<ConsultationsView>('status');
  const byStatus = chart.metrics.find((m) => m.slug === 'consultations-by-status')?.extras?.byStatus;
  const bySource = chart.metrics.find((m) => m.slug === 'consultations-by-source')?.extras?.bySource;
  const status = useMemo(() => buildStatusStackRows(byStatus, year, STATUSES), [byStatus, year]);
  const source = useMemo(() => buildSourceRows(bySource, year, SOURCE_ORDER), [bySource, year]);
  const lastStatus = status.keys[status.keys.length - 1];
  const yearTotals = useMemo(() => source.keys.map((k) => {
    const total = source.rows.reduce((sum, r) => sum + (r.cells[k]?.total ?? 0), 0);
    const paid = source.rows.reduce((sum, r) => sum + (r.cells[k]?.paid ?? 0), 0);
    return { key: k, total, paid };
  }), [source]);

  const xAxis = (
    <XAxis
      dataKey="monthLabel"
      tick={{ fontSize: 11 }}
      angle={-32}
      textAnchor="end"
      height={52}
      interval={0}
    />
  );

  return (
    <>
      <div className="stats-segments stats-segments--card" role="group" aria-label="View">
        {(['status', 'source'] as const).map((v) => (
          <button
            key={v}
            type="button"
            className={`stats-segments__btn ${view === v ? 'is-active' : ''}`}
            aria-pressed={view === v}
            onClick={() => setView(v)}
          >
            {v === 'status' ? 'By status' : 'By source'}
          </button>
        ))}
      </div>
      {view === 'source' && yearTotals.length > 0 && (
        <div className="stats-source-summary" aria-label="Year totals by source">
          {yearTotals.map((t) => (
            <span key={t.key} className="stats-source-summary__item">
              <span className="stats-source-summary__swatch" style={{ background: SOURCE_COLORS[t.key] ?? '#a0aec0' }} />
              <strong>{SOURCE_LABELS[t.key] ?? t.key}</strong> {t.total} · {t.paid} paid
              {t.total > 0 && ` · ${pct((t.paid / t.total) * 100)}`}
            </span>
          ))}
        </div>
      )}
      <div className="stats-chart">
        <ResponsiveContainer width="100%" height="100%">
          {view === 'status' ? (
            <BarChart data={status.rows} margin={{ top: 20, right: 12, left: 8, bottom: 56 }}>
              <CartesianGrid strokeDasharray="3 3" />
              {xAxis}
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip content={<StatusTooltip keys={status.keys} />} />
              <Legend />
              {status.keys.map((k) => (
                <Bar
                  key={k}
                  dataKey={k}
                  stackId="status"
                  fill={statusColor(k)}
                  name={statusLabel(k)}
                  maxBarSize={36}
                  isAnimationActive={false}
                >
                  {k === lastStatus && (
                    <LabelList
                      position="top"
                      fontSize={11}
                      fill="#4a5568"
                      valueAccessor={(entry: { payload: BreakdownRow }) => (
                        entry.payload.total > 0 ? entry.payload.total : ''
                      )}
                    />
                  )}
                </Bar>
              ))}
            </BarChart>
          ) : (
            <ComposedChart data={source.rows} margin={{ top: 20, right: 12, left: 8, bottom: 56 }}>
              <CartesianGrid strokeDasharray="3 3" />
              {xAxis}
              <YAxis yAxisId="count" allowDecimals={false} tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="rate"
                orientation="right"
                domain={[0, 100]}
                tickFormatter={(v) => pct(Number(v))}
                tick={{ fontSize: 12 }}
                width={44}
              />
              <Tooltip content={<SourceTooltip keys={source.keys} />} />
              <Legend />
              {source.keys.map((k) => (
                <Bar
                  key={k}
                  yAxisId="count"
                  dataKey={k}
                  fill={SOURCE_COLORS[k] ?? '#a0aec0'}
                  name={SOURCE_LABELS[k] ?? k}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={24}
                  isAnimationActive={false}
                />
              ))}
              {source.keys.filter((k) => RATE_SOURCES.includes(k)).map((k) => (
                <Line
                  key={rateKey(k)}
                  yAxisId="rate"
                  type="linear"
                  dataKey={rateKey(k)}
                  stroke={SOURCE_COLORS[k] ?? '#a0aec0'}
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  dot={{ r: 3 }}
                  name={`${SOURCE_LABELS[k] ?? k} paid %`}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
      {view === 'source' && (
        <p className="stats-chart-note">
          Paid % = paid ÷ consultations that month. Recent months still have undecided consultations, so their rate can rise.
        </p>
      )}
    </>
  );
};

const SpendTooltip: React.FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{ payload: SpendRow }>;
  currentMonth?: string;
}> = ({ active, label, payload, currentMonth }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const value = row.actual ?? row.planned ?? 0;
  const planned = !!currentMonth && row.monthKey > currentMonth;
  return (
    <div className="stats-tooltip">
      <div className="stats-tooltip__label">{label}</div>
      <div className="stats-tooltip__row stats-tooltip__row--cost">
        {planned ? 'Planned' : 'Spent'}: {usd.format(value)}
      </div>
      {row.monthKey === currentMonth && (
        <div className="stats-tooltip__row stats-tooltip__row--muted">Includes the rest of this month</div>
      )}
    </div>
  );
};

/** Ad spend per month; ads spanning months are split by days on the API. Future months dashed. */
const SpendLineChart: React.FC<{ chart: StatsChartView; year: number }> = ({ chart, year }) => {
  const metric = chart.metrics[0];
  const currentMonth = metric?.extras?.currentMonth;
  const rows = useMemo(
    () => buildSpendRows(metric?.series, year, currentMonth),
    [metric?.series, year, currentMonth],
  );
  const color = metric?.color ?? '#dd6b20';
  return (
    <div className="stats-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 20, right: 12, left: 8, bottom: 56 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="monthLabel"
            tick={{ fontSize: 11 }}
            angle={-32}
            textAnchor="end"
            height={52}
            interval={0}
          />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => usd.format(Number(v))} width={64} />
          <Tooltip content={<SpendTooltip currentMonth={currentMonth} />} />
          <Line
            type="linear"
            dataKey="actual"
            stroke={color}
            strokeWidth={2}
            dot={{ r: 3 }}
            name={metric?.seriesLabel ?? 'Spent'}
            connectNulls={false}
            isAnimationActive={false}
          />
          <Line
            type="linear"
            dataKey="planned"
            stroke={color}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={{ r: 3, strokeDasharray: '' }}
            name="Planned"
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const StatsChart: React.FC<{ chart: StatsChartView; year: number }> = ({ chart, year }) => {
  const groupedData = useMemo(
    () => buildGroupedMonthlyRows(chart.metrics, year),
    [chart.metrics, year],
  );

  const prediction = chart.options.prediction
    ? chart.metrics[0]?.extras?.prediction ?? null
    : null;
  const stackedData = useMemo(
    () => buildStackedPredictionRows(chart.metrics[0]?.series ?? [], year, prediction),
    [chart.metrics, year, prediction],
  );

  const unit = chart.metrics[0]?.unit ?? 'count';

  if (chart.chartType === 'consultations-breakdown') {
    return <ConsultationsBreakdownChart key={chart.slug} chart={chart} year={year} />;
  }

  if (chart.chartType === 'line') {
    return <SpendLineChart chart={chart} year={year} />;
  }

  if (chart.chartType === 'rate-bar') {
    return <RateChart key={chart.slug} chart={chart} year={year} />;
  }

  if (chart.chartType === 'stacked-bar') {
    const color = chart.metrics[0]?.color ?? '#667eea';
    return (
      <div className="stats-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={stackedData} margin={{ top: 8, right: 12, left: 8, bottom: 56 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="monthLabel"
              tick={{ fontSize: 11 }}
              angle={-32}
              textAnchor="end"
              height={52}
              interval={0}
            />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (Number.isInteger(v) ? String(v) : v.toFixed(0))} />
            <Tooltip content={<StackedTooltip />} />
            {chart.options.legend && <Legend />}
            <Bar dataKey="value" stackId="rev" fill={color} name={chart.metrics[0]?.seriesLabel ?? 'Collected'} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            {chart.options.prediction && (
              <Bar
                dataKey="predicted"
                stackId="rev"
                fill={color}
                fillOpacity={0.28}
                stroke={color}
                strokeDasharray="4 3"
                name="Predicted"
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="stats-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={groupedData} margin={{ top: 8, right: 12, left: 8, bottom: 56 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="monthLabel"
            tick={{ fontSize: 11 }}
            angle={-32}
            textAnchor="end"
            height={52}
            interval={0}
          />
          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip content={<GroupedTooltip metrics={chart.metrics} unit={unit} showNet={chart.options.showNet} />} />
          {chart.options.legend !== false && <Legend />}
          {chart.metrics.map((m) => (
            <Bar
              key={m.slug}
              dataKey={m.slug}
              fill={m.color}
              name={m.seriesLabel}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Catalog-driven picker: segmented while the list is small, react-select after that. */
function ChartPicker({
  catalog,
  value,
  onChange,
}: {
  catalog: StatsChartMeta[];
  value: string;
  onChange: (slug: string) => void;
}) {
  if (catalog.length === 0) return null;

  if (catalog.length <= SELECTOR_SEGMENT_LIMIT) {
    return (
      <div className="stats-segments" role="group" aria-label="Chart">
        {catalog.map((c) => (
          <button
            key={c.slug}
            type="button"
            className={`stats-segments__btn ${value === c.slug ? 'is-active' : ''}`}
            onClick={() => onChange(c.slug)}
          >
            {c.selectorLabel}
          </button>
        ))}
      </div>
    );
  }

  const options: SelectOption<string>[] = catalog.map((c) => ({
    value: c.slug,
    label: c.selectorLabel,
  }));
  const selected = options.find((o) => o.value === value) ?? options[0];
  return (
    <div className="stats-chart-select">
      <Select
        value={selected}
        onChange={(opt) => { if (opt) onChange(opt.value); }}
        options={options}
        isClearable={false}
        placeholder="Chart"
      />
    </div>
  );
}

const Stats: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { year, chart, catalog, minYear, loading, error } = useAppSelector((state) => state.stats);
  const floor = Math.max(STATS_YEAR_MIN, minYear);
  const chartSlug = searchParams.get('chart') || catalog[0]?.slug || DEFAULT_STATS_CHART;

  useEffect(() => {
    dispatch(fetchStatsAsync({ year, chart: chartSlug }));
  }, [dispatch, year, chartSlug]);

  const goPrev = useCallback(() => {
    dispatch(setStatsYear(year - 1));
  }, [dispatch, year]);

  const goNext = useCallback(() => {
    dispatch(setStatsYear(year + 1));
  }, [dispatch, year]);

  const selectChart = useCallback((slug: string) => {
    setSearchParams(slug === DEFAULT_STATS_CHART ? {} : { chart: slug });
  }, [setSearchParams]);

  const heading = chart?.label ?? 'Stats';

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Stats</h1>
          <p>One chart at a time — switch the metric in the bar below</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filter-bar">
        <ChartPicker catalog={catalog} value={chartSlug} onChange={selectChart} />
        <span className="filter-sep filter-sep--divider" aria-hidden="true" />
        <button
          type="button"
          className="btn-secondary"
          onClick={goPrev}
          disabled={year <= floor}
          aria-label="Previous year"
        >
          ←
        </button>
        <span className="stats-year-label">{year}</span>
        <button
          type="button"
          className="btn-secondary"
          onClick={goNext}
          aria-label="Next year"
        >
          →
        </button>
      </div>

      {loading && !chart ? (
        <LoadingSpinner />
      ) : chart ? (
        <section className="stats-chart-card" aria-labelledby="stats-chart-title">
          <h2 id="stats-chart-title">{heading}</h2>
          <StatsChart chart={chart} year={year} />
        </section>
      ) : null}
    </div>
  );
};

export default Stats;
